const TIME_FIELD_SELECTOR = '[data-resource-availability-time]'
const TIME_FIELD_NAMES = new Set(['starts_at', 'ends_at'])
const START_FIELD_SELECTOR = '[data-resource-availability-time="starts_at"]'
const END_FIELD_SELECTOR = '[data-resource-availability-time="ends_at"]'

function timeMinutes(value) {
  const match = /^(\d{2}):(\d{2})$/.exec(String(value ?? ''))
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])

  return hours <= 23 && minutes <= 59 ? hours * 60 + minutes : null
}

function formattedTime(minutes) {
  if (minutes === 1440) return '00:00'

  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

function shiftedEnd(previousStart, nextStart, currentEnd) {
  const nextStartMinute = timeMinutes(nextStart)
  if (nextStartMinute === null) return currentEnd

  if (!currentEnd) {
    const defaultEnd = nextStartMinute + 60

    return defaultEnd <= 1440 ? formattedTime(defaultEnd) : currentEnd
  }

  const previousStartMinute = timeMinutes(previousStart)
  const currentEndMinute = timeMinutes(currentEnd)
  if (previousStartMinute === null || currentEndMinute === null) return currentEnd

  const normalizedEnd = currentEndMinute === 0 && previousStartMinute > 0
    ? 1440
    : currentEndMinute
  const duration = normalizedEnd - previousStartMinute
  const nextEnd = nextStartMinute + duration

  return duration > 0 && nextEnd > nextStartMinute && nextEnd <= 1440
    ? formattedTime(nextEnd)
    : currentEnd
}

export default () => ({
  row: null,
  inputListener: null,
  focusListener: null,
  stopWatchingCommits: null,
  previousStart: null,

  init() {
    this.row = this.$el?.closest?.('.form-repeater') ?? null
    this.inputListener = (event) => this.handleInput(event)
    this.focusListener = (event) => this.rememberFocusedStart(event)
    this.row?.addEventListener('input', this.inputListener)
    this.row?.addEventListener('focusin', this.focusListener)
    this.watchLivewireCommits()
    this.syncVisibility()

    const remember = () => this.rememberStart()
    if (typeof this.$nextTick === 'function') {
      this.$nextTick(remember)
    } else {
      queueMicrotask(remember)
    }
  },

  destroy() {
    if (this.inputListener) this.row?.removeEventListener('input', this.inputListener)
    if (this.focusListener) this.row?.removeEventListener('focusin', this.focusListener)
    this.stopWatchingCommits?.()

    this.row = null
    this.inputListener = null
    this.focusListener = null
    this.stopWatchingCommits = null
  },

  watchLivewireCommits() {
    this.stopWatchingCommits?.()
    this.stopWatchingCommits = null

    if (typeof window.Livewire?.hook !== 'function') return

    const wireId = this.$el?.closest?.('[wire\\:id]')?.getAttribute('wire:id')
    if (!wireId) return

    this.stopWatchingCommits = window.Livewire.hook('commit', ({ component, succeed }) => {
      if (component?.id !== wireId || typeof succeed !== 'function') return

      succeed(() => queueMicrotask(() => this.syncVisibility()))
    })
  },

  syncVisibility() {
    const row = this.row ?? this.$el?.closest?.('.form-repeater')
    if (!row) return

    const allDay = this.$el.checked === true

    row.querySelectorAll(TIME_FIELD_SELECTOR).forEach((input) => {
      if (!TIME_FIELD_NAMES.has(input.getAttribute('data-resource-availability-time'))) return

      const field = input.closest('.form-element')
      if (!field || field.closest('.form-repeater') !== row) return

      field.toggleAttribute('hidden', allDay)
      field.setAttribute('aria-hidden', allDay ? 'true' : 'false')
      field.style.display = allDay ? 'none' : ''
    })
  },

  handleInput(event) {
    if (event.target === this.$el) {
      this.syncVisibility()

      return
    }

    if (!(event.target instanceof HTMLInputElement) || !event.target.matches(START_FIELD_SELECTOR)) {
      return
    }

    const nextStart = this.fieldValue(event.target)
    if (timeMinutes(nextStart) === null) return

    const endInput = this.row?.querySelector(END_FIELD_SELECTOR)
    if (!(endInput instanceof HTMLInputElement)) {
      this.previousStart = nextStart

      return
    }

    const currentEnd = this.fieldValue(endInput)
    const nextEnd = shiftedEnd(this.previousStart, nextStart, currentEnd)
    if (nextEnd !== currentEnd) this.setFieldValue(endInput, nextEnd)

    this.previousStart = nextStart
  },

  rememberFocusedStart(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches(START_FIELD_SELECTOR)) {
      return
    }

    this.previousStart = this.fieldValue(event.target)
  },

  rememberStart() {
    const input = this.row?.querySelector(START_FIELD_SELECTOR)
    if (input instanceof HTMLInputElement) this.previousStart = this.fieldValue(input)
  },

  fieldValue(input) {
    const state = this.flatpickrState(input)

    return typeof state?.value === 'string' ? state.value : input.value
  },

  setFieldValue(input, value) {
    const state = this.flatpickrState(input)
    if (state && 'value' in state) {
      state.value = value

      return
    }

    input.value = value
    input.dispatchEvent(new Event('input', { bubbles: true }))
  },

  flatpickrState(input) {
    const root = input.closest('[x-data^="flatpickrField"]')
    if (!root || !window.Alpine?.$data) return null

    try {
      return window.Alpine.$data(root)
    } catch {
      return null
    }
  }
})
